import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomLink from "./customLink";

import ContactForm from "./ContactForm";
import SliderRating from "./SliderRating";
import AppStoreButtons from "../../components/AppStoreButtons";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { GAEventTrack } from "../../utils/hooks/GA_events";
import StudentAccordionPage from "../faq-studentPage";
import { pageMetadata } from "../../config/metadata";
import { studentFAQ } from "../../config/faqSchema";
import { organizationSchema } from "../../config/organizationSchema"
import { searchSchema } from "../../config/searchSchema"
import SEO from "../../Seo";

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const StudentLandingPage = ({ placement }) => {
  const metadata = pageMetadata.studentSeo;
  document.title = metadata.title;
  document.description = metadata.description;

  const navigate = useNavigate();
  const clickToScroll = (e) => {
    e.preventDefault();
    const targetId = e.target.getAttribute("href");
    if (placement === "landingPage") {
      const targetElement = document.querySelector(targetId);
      const offset = targetElement.offsetTop;
      window.scrollTo({
        top: offset,
        behavior: "smooth",
      });
    } else {
      navigate(`/${targetId}`);
    }
  };

  const devMode = false;

  const panel1 = useRef();
  const panel2 = useRef();
  const panel3 = useRef();
  const panel4 = useRef();
  const contactFormRef = useRef(null);
  const setFormRef = (ref) => {
    contactFormRef.current = ref;
  };

  useGSAP(() => {
    let mm = gsap.matchMedia();

    var t0 = gsap.timeline();
    t0.to("#feature-data", {
      scrollTrigger: {
        trigger: ".gsap-panel-1",
        start: "top bottom",
        markers: false,
        onEnter: () => {
          gsap.to("#feature-data", { background: "#FF8E1F" });
        },
      },
    });
    t0.to("#feature-data", {
      scrollTrigger: {
        trigger: ".gsap-panel-2",
        start: "top top",
        markers: false,
        onEnter: () => {
          gsap.to("#feature-data", { background: "#246CFF" });
        },
        onLeaveBack: () => {
          gsap.to("#feature-data", { background: "#FF8E1F" });
        },
      },
    });
    t0.to("#feature-data", {
      scrollTrigger: {
        trigger: ".gsap-panel-3",
        start: "top top",
        markers: false,
        onEnter: () => {
          gsap.to("#feature-data", { background: "#3DC1CA" });
        },
        onLeaveBack: () => {
          gsap.to("#feature-data", { background: "#246CFF" });
        },
      },
    });

    t0.to("#feature-data", {
      scrollTrigger: {
        trigger: ".gsap-panel-4",
        start: "top top",
        markers: false,
        onEnter: () => {
          gsap.to("#feature-data", { background: "#00256F" });
        },
        onLeaveBack: () => {
          gsap.to("#feature-data", { background: "#3DC1CA" });
        },
      },
    });

    mm.add("(min-width:992px)", (context) => {
      // panel 1
      gsap.timeline({
        scrollTrigger: {
          trigger: ".gsap-panel-1",
          scrub: 0.1,
          pin: true,
          pinSpacing: false,
          start: "top top",
          end: "bottom top",
          markers: false,
          onEnter: () => {
            panel1.current.classList.remove("g-fade-out");
            panel1.current.classList.add("g-fade-in");
          },
          onLeave: () => {
            panel1.current.classList.add("g-fade-out");
            panel1.current.classList.remove("g-fade-in");
          },
          onEnterBack: () => {
            panel1.current.classList.remove("g-fade-out");
            panel1.current.classList.add("g-fade-in");
          },
          onLeaveBack: () => {
            panel1.current.classList.add("g-fade-out");
            panel1.current.classList.remove("g-fade-in");
          },
        },
      });

      // panel 2
      gsap.timeline({
        scrollTrigger: {
          trigger: ".gsap-panel-2",
          scrub: 0.1,
          pin: true,
          pinSpacing: false,
          start: "top top",
          end: "bottom top",
          markers: false,
          onEnter: () => {
            panel2.current.classList.remove("g-fade-out");
            panel2.current.classList.add("g-fade-in");
          },
          onLeave: () => {
            panel2.current.classList.add("g-fade-out");
            panel2.current.classList.remove("g-fade-in");
          },
          onEnterBack: () => {
            panel2.current.classList.remove("g-fade-out");
            panel2.current.classList.add("g-fade-in");
          },
          onLeaveBack: () => {
            panel2.current.classList.add("g-fade-out");
            panel2.current.classList.remove("g-fade-in");
          },
        },
      });

      // panel 3
      gsap.timeline({
        scrollTrigger: {
          trigger: ".gsap-panel-3",
          scrub: 1,
          pin: true,
          pinSpacing: false,
          start: "top top",
          end: "bottom top",
          markers: false,
          onEnter: () => {
            panel3.current.classList.remove("g-fade-out");
            panel3.current.classList.add("g-fade-in");
          },
          onLeave: () => {
            panel3.current.classList.add("g-fade-out");
            panel3.current.classList.remove("g-fade-in");
          },
          onEnterBack: () => {
            panel3.current.classList.remove("g-fade-out");
            panel3.current.classList.add("g-fade-in");
          },
          onLeaveBack: () => {
            panel3.current.classList.add("g-fade-out");
            panel3.current.classList.remove("g-fade-in");
          },
        },
      });

      // panel 4
      gsap.timeline({
        scrollTrigger: {
          trigger: ".gsap-panel-4",
          scrub: 0.1,
          pin: true,
          pinSpacing: true,
          start: "top top",
          end: "bottom top",
          markers: false,
          onEnter: () => {
            panel4.current.classList.remove("g-fade-out");
            panel4.current.classList.add("g-fade-in");
          },
          onLeave: () => {
            panel4.current.classList.add("g-fade-out");
            panel4.current.classList.remove("g-fade-in");
          },
          onEnterBack: () => {
            panel4.current.classList.remove("g-fade-out");
            panel4.current.classList.add("g-fade-in");
          },
          onLeaveBack: () => {
            panel4.current.classList.add("g-fade-out");
            panel4.current.classList.remove("g-fade-in");
          },
        },
      });

      gsap.to("#tab1-pill", {
        scrollTrigger: {
          trigger: "#tab1",
          start: "top center",
          end: "bottom top",
          toggleClass: { targets: "#tab1-pill", className: "active" },
        },
      });
      gsap.to("#tab2-pill", {
        scrollTrigger: {
          trigger: "#tab2",
          start: "top top",
          end: "bottom top",
          toggleClass: { targets: "#tab2-pill", className: "active" },
        },
      });
      gsap.to("#tab3-pill", {
        scrollTrigger: {
          trigger: "#tab3",
          start: "top top",
          end: "bottom top",
          toggleClass: { targets: "#tab3-pill", className: "active" },
        },
      });
      gsap.to("#tab4-pill", {
        scrollTrigger: {
          trigger: "#tab4",
          start: "top top",
          end: "bottom top",
          toggleClass: { targets: "#tab4-pill", className: "active" },
        },
      });
      gsap.to("#tab5-pill", {
        scrollTrigger: {
          trigger: "#tab5",
          start: "top top",
          end: "bottom top",
          toggleClass: { targets: "#tab5-pill", className: "active" },
        },
      });
    });
  }); // useGSAP()

  const [activeTab, setActiveTab] = useState(
    localStorage.getItem("activeTab") || ""
  );
  const [showSection, setShowSection] = useState(
    localStorage.getItem("showSection") || ""
  );

  useEffect(() => {
    const tabObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newActiveTab = entry.target.id;
          setActiveTab(newActiveTab);
          localStorage.setItem("activeTab", newActiveTab);
          document
            .querySelector(".feature-section")
            .classList.add("active-mode");
        }
      });
    });

    const secObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const newShowSection = entry.target.id;
            setShowSection(newShowSection);
            localStorage.setItem("showSection", newShowSection);
          }
        });
      },
      { rootMargin: "-50px 0px 0px 0px" } // Adjust rootMargin to trigger earlier when scrolling up
    );

    // Observe each tab-section
    document.querySelectorAll(".tab-section").forEach((tab) => {
      tabObserver.observe(tab);
    });

    // Observe each section
    document.querySelectorAll(".section").forEach((sec) => {
      secObserver.observe(sec);
    });

    // Clean up observer on component unmount
    return () => {
      tabObserver.disconnect();
      secObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset === 0) {
        document
          .querySelector(".feature-section")
          .classList.remove("active-mode");
      }
    };

    setTimeout(function () {
      ScrollTrigger.refresh();
    }, 2000);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function handleTabClick(name = "", e = null) {
    if (e) {
      e.preventDefault();
    }
    let el = document.getElementById(name);
    if (el) {
      let top = el.getBoundingClientRect().top;
      let y = top + window.scrollY;
      if (top < 0) {
        y = y - 10;
      } else {
        y = y + 10;
      }
      // console.log(name, y, top, window.scrollY);
      window.scroll({
        top: y,
        behavior: "smooth",
      });
    }
  }

  // back to top button set
  useEffect(() => {
    const handleScroll = () => {
      const scrollToTopBtn = document.querySelector(".scrollToTopBtn");
      const rootElement = document.documentElement;

      if (rootElement.scrollTop >= 300) {
        scrollToTopBtn.classList.add("showBtn");
      } else {
        scrollToTopBtn.classList.remove("showBtn");
      }
    };

    const scrollToTop = () => {
      document.documentElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    const scrollToTopBtn = document.querySelector(".scrollToTopBtn");
    scrollToTopBtn.addEventListener("click", scrollToTop);
    document.addEventListener("scroll", handleScroll);

    return () => {
      scrollToTopBtn.removeEventListener("click", scrollToTop);
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // Animate the #banner-cloud element
    gsap.to("#banner-cloud", {
      duration: 1,
      y: "-=12",
      ease: "Power1.easeInOut",
      yoyo: true,
      repeat: -1,
      delay: 0.5,
    });

    // Animate the #banner-heading element
    gsap.to("#banner-heading", {
      duration: 1,
      y: "-=12",
      ease: "Power1.easeInOut",
      yoyo: true,
      repeat: -1,
      delay: 0.75,
    });

    gsap.fromTo(
      "#events-first",
      { x: "10%" }, // Start off-screen to the right
      {
        duration: 1,
        x: "0%", // Move to its original position
        ease: "Power1.easeInOut",
        yoyo: true,
        repeat: -1, // Infinite repeat
        delay: 0.5,
      }
    );
  }, []);

  gsap.to("#build-wrapper-img", {
    duration: 1,
    y: "-=12",
    ease: "Power1.easeInOut",
    yoyo: true,
    repeat: -1,
    delay: 0.5,
    autoAlpha: 1, // Ensures the element is visible initially
  });

  gsap.from("#build-wrapper-img", {
    duration: 1.5,
    y: 20,
    autoAlpha: 0,
    ease: "Power1.easeInOut",
    delay: 0.5,
    scrollTrigger: {
      trigger: "#build-wrapper-img",
      start: "top 80%", // Adjust the start position as needed
      toggleActions: "play none none none",
    },
  });

  const buildSlider = {
    dots: false,
    arrows: true,
    infinite: true,
    autoplay: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          autoplay: true,
        },
      },
    ],
  };

  //loader code
  const [loaderVisible, setLoaderVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaderVisible(false);
      window.scrollTo(0, 0);
    }, 1000);

    return () => clearTimeout(timer); // Cleanup the timer on component unmount
  }, []);

  return (
    <>
    <SEO
        title={metadata.title}
        description={metadata.description}
        keywords={metadata.keywords}
        canonical={metadata.canonical}
        ogTitle={metadata.ogTitle}
        ogDescription={metadata.ogDescription}
        ogImage={metadata.ogImage}
        ogUrl={metadata.ogUrl}
        ogType={metadata.ogType}
        twitterCard={metadata.twitterCard}
        twitterTitle={metadata.twitterTitle}
        twitterDescription={metadata.twitterDescription}
        faqData={studentFAQ}
        organizationSchema={organizationSchema}
        searchSchema={searchSchema}
      />
      {loaderVisible && (
        <div className="home-loader">
          <img src="/img/loader.gif" alt="Loading" />
        </div>
      )}
      <div className="back-to-top">
        <button className="scrollToTopBtn">
          <img src="/img/new-home/back-to-top.png" alt="back-to-top" />
        </button>
      </div>
      <section className="banner-section home-banner-section student-banner">
        <div className="container">
          <div className="row register-section">
            <div className="flex items-center justify-center my-10">
              <div className="col-md-12 col-lg-7 text-center quote-head">
                The only way to do great work is to love what you do and to
                practice it endlessly.
                <span className="block text-xl font-thin mt-2">
                  - Steve Jobs
                </span>
              </div>
            </div>

            {/* <div className="col-md-12 col-lg-6">
              <div className="content">
                <div className="banner-img">
                  <img
                    id="banner-cloud"
                    className="banner-cloud"
                    src="/img/new-home/banner-cloud.png"
                    alt="banner"
                  />
                  <img
                    id="banner-heading"
                    className="banner-heading"
                    src="/img/new-home/banner-heading.png"
                    alt="banner"
                  />
                </div>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 right-item">
              <h4 data-aos="zoom-in" data-aos-delay="200">
                Test your knowledge and win fantastic prizes in our Practido
                Online Olympiad!
              </h4>
              <div className="btn-space">
                <button
                  data-aos="zoom-in"
                  data-aos-delay="200"
                  data-aos-duration="800"
                  type="button"
                  className="btn primary-btn"
                  onClick={() => {
                    GAEventTrack({
                      event: "register_btn_click",
                      data: { button_name: "Register Now" },
                    });

                    window.open("/register", "_blank");
                  }}
                >
                  Register Now
                </button>
              </div>
            </div> */}
          </div>
          {/* row */}
        </div>
        <div className="student-blue-wrapper">
          <div className="container">
            {/* <h4>
              The only way to do great work is to love what you do and to
              practice it endlessly.<span>-Steve Jobs</span>
            </h4> */}
            <div className="book-wrapper">
              <img
                id="banner-cloud"
                className="banner-cloud book-icon"
                src="/img/student-home/book-icon.webp"
                alt="book"
              />
              <img
                id="banner-heading"
                className="banner-heading book-circle"
                src="/img/student-home/book-circle.webp"
                alt="banner"
              />
            </div>
            <h1>
              We are an E-Learning Online Platform That Makes Practice Feel Like Play
            </h1>
            <p>
              <span>Let’s be honest: students love learning new things, but practice? That’s where most lose steam. Practido bridges that gap.</span><br/> 
              Our mission is simple and bold: turn practice into something students actually enjoy, so they don’t just learn… they master.
              Unlike most online learning platforms for students that only focus on lessons, Practido puts the spotlight on practice, giving students the structured repetition they need without the boredom that usually comes with it.
            </p>
            <p className="d-none">
              <span className="bold">Free Learning Starts Now!</span> Download
              App:
            </p>
            <div className="banner-icon d-none">
              <span>
                <img src="/img/student-home/white-apple.png" alt="apple" />
              </span>
              <span>
                <img
                  src="/img/student-home/white-play-store.png"
                  alt="play-store"
                />
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="resource-section">
        <div className="container aos-animate" data-aos="fade-up">
          <div
            className="head"
            data-aos="fade-up"
            data-aos-delay="150"
            data-aos-duration="500"
          >
            <h2>
              Your All-in-One E Learning Online Platform 
              <span>
                Whether you're a school, an educator, a parent, or a student, Practido delivers a full ecosystem designed for real academic growth. Here’s how each group benefits:
              </span>
            </h2>
          </div>
          <div className="row">
            <div className="col-sm-12 col-md-4">
              <div data-aos="zoom-in" className="img-box">
                <img src="/img/student-home/rec-3.webp" alt="schools" />
              </div>
              <h3>For Schools</h3>
              <p>
                Supercharge your school’s academic culture with Practido’s Online Exam Test ecosystem.<br/>
                Our expert-crafted assessments in English, Maths, Computer and Science create a secure, flexible digital environment that sets students up for excellence. Schools joining the Practido community also unlock exclusive discounts on upcoming initiatives- teacher training, learning apps, school management tools, interactive lab activities, and more.
              </p>
              <CustomLink
                to="/practido-for-schools"
                type="button"
                className="btn link-btn full-wid-btn"
              >
                Register Your School
              </CustomLink>
            </div>
            <div className="col-sm-12 col-md-4">
              <div data-aos="zoom-in" className="img-box">
                <img src="/img/student-home/rec-2.webp" alt="educators" />
              </div>
              <h3>For Educators</h3>
              <p>
                Become part of a movement shaping the next generation of learners.<br/>
                Practido invites educators to contribute world-class content in Maths, Science, English and Computer, and yes, you earn competitive rewards and even royalties. If you’ve ever wanted to impact millions through an e learning online platform, this is your moment.
              </p>
              <CustomLink
                to="/practido-for-educators"
                type="button"
                className="btn link-btn full-wid-btn"
              >
                Join as an Educator
              </CustomLink>
            </div>
            <div className="col-sm-12 col-md-4">
              <div data-aos="zoom-in" className="img-box">
                <img src="/img/student-home/rec-1.webp" alt="parents" />
              </div>
              <h3>For Parents</h3>
              <p>
                Finally, an app that helps parents stay genuinely connected to their child’s learning.<br/>
                Assign tasks, track progress, monitor strengths and weaknesses, and get real-time insight into academic growth. Everything you need to guide smarter learning is now in your pocket.
              </p>
              <CustomLink
                to="/practido-for-parents"
                type="button"
                className="btn link-btn full-wid-btn"
              >
                Begin Parenting Journey
              </CustomLink>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-section">
        <h2 className="pd main-head">
          Features That Make Learning Addictive<br/>(In a Good Way)
          <span>
            Practido isn’t just another app. It’s a full suite of tools designed to keep kids engaged, curious, and consistently improving.
          </span>
        </h2>
        <div id="feature-data" className="feature-data">
          <div className={`tab-section`} id="tab1">
            <div
              id="section1"
              className="gsap-panel gsap-panel-1 section feature-wrapper mb-0 show"
            >
              <div className="container g-fade-out" ref={panel1}>
                <div id="practice" className="feature-box row aos-animate">
                  <div
                    className="col-md-12 col-lg-5 center"
                    data-aos="fade-right"
                    data-aos-delay="800"
                  >
                    <img
                      className="feature-img"
                      src="/img/student-home/feature-1.webp"
                      alt="apple"
                    />
                  </div>
                  <div
                    className="col-md-12 col-offset-1 col-lg-6 para-box"
                    data-aos="fade-left"
                    data-aos-delay="800"
                  >
                    <h3>Topic-Based Practice Tests</h3>
                    <p>
                      From English grammar to Science concepts, Maths drills, and computer topics, your child can choose any topic, take a practice test, and sharpen skills at their own pace. Every test comes with meaningful feedback that actually helps.
                    </p>
                    <h4>Free Learning Starts Now!</h4>
                    <AppStoreButtons variant="white" />
                  </div>
                </div>
              </div>
            </div>
            <div
              className="gsap-panel gsap-panel-2 section feature-wrapper mb-0 show"
              id="section2"
            >
              <div className="container g-fade-out" ref={panel2}>
                <div className="feature-box row aos-animate">
                  <div
                    className="col-md-12 col-lg-5 center"
                    data-aos="fade-right"
                    data-aos-delay="800"
                  >
                    <img
                      className="feature-img"
                      src="/img/student-home/feature-2.webp"
                      alt="apple"
                    />
                  </div>
                  <div
                    className="col-md-12 col-offset-1 col-lg-6 para-box"
                    data-aos="fade-left"
                    data-aos-delay="800"
                  >
                    <h3>Timed Model Tests for Exam Success</h3>
                    <p>
                      Students preparing for competitive exams or even the International Mathematical Olympiad can benefit big time.<br/>
                      Our Model Test feature generates 35 random questions from multiple topics, timed for one hour, just like a real online exam test. Perfect for building speed, accuracy, and confidence.
                    </p>
                    <h4>Free Learning Starts Now!</h4>
                    <AppStoreButtons variant="white" />
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`gsap-panel gsap-panel-3 section feature-wrapper mb-0 ${
                showSection === "section3" ? "show" : "hide"
              }`}
              id="section3"
            >
              <div className="container g-fade-out" ref={panel3}>
                <div className="feature-box row aos-animate">
                  <div
                    className="col-md-12 col-lg-5 center"
                    data-aos="fade-right"
                    data-aos-delay="800"
                  >
                    <img
                      className="feature-img"
                      src="/img/student-home/feature-3.webp"
                      alt="apple"
                    />
                  </div>
                  <div
                    className="col-md-12 col-offset-1 col-lg-6 para-box"
                    data-aos="fade-left"
                    data-aos-delay="800"
                  >
                    <h3>Track Your Entire Test History</h3>
                    <p>
                      No more guessing where your child stands.<br/>
                      Practido breaks down past tests, average scores, mastery over topics practised with topic-wise scores, and even correct vs. incorrect answers. It’s targeted learning made effortless.
                    </p>
                    <h4>Free Learning Starts Now!</h4>
                    <AppStoreButtons variant="white" />
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`gsap-panel gsap-panel-4 section feature-wrapper mb-0 ${
                showSection === "section4" ? "show" : "hide"
              }`}
              id="section4"
            >
              <div className="container g-fade-out" ref={panel4}>
                <div className="feature-box row aos-animate">
                  <div
                    className="col-md-12 col-lg-5 center"
                    data-aos="fade-right"
                    data-aos-delay="800"
                  >
                    <img
                      className="feature-img"
                      src="/img/student-home/feature-4.webp"
                      alt="apple"
                    />
                  </div>
                  <div
                    className="col-md-12 col-offset-1 col-lg-6 para-box"
                    data-aos="fade-left"
                    data-aos-delay="800"
                  >
                    <h3>Instant Results Anytime</h3>
                    <p>
                      Why wait? Get scores the moment a test ends.<br/>
                      Students can review answers, share results with friends, revisit the test, and even download a PDF for revision or portfolio building. Stay informed, track your progress, and improve with ease!”
                    </p>
                    <h4>Free Learning Starts Now!</h4>
                    <AppStoreButtons variant="white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab-section */}

          <div className="tab-section ">
            <div className="section purple-back mb-0 feature-wrapper">
              <div className="container">
                <div className="feature-box row pd100">
                  <div className="col-offset-2 col-md-8 para-box">
                    <h3 className="center">My World – Where Learning Meets Fun</h3>
                    <p className="center">
                      Students can jump into Olympiads, challenge their friends, explore events, and try to earn a spot in the Hall of Fame. Learning feels like a game, and that’s exactly the point.
                    </p>
                  </div>
                  <div className="col-md-12 purple-col">
                    <div className="left-img-frame aos-animate">
                      <p data-aos="fade-left" data-aos-delay="200">
                        Exciting Events
                      </p>
                      <img
                        data-aos="fade-left"
                        data-aos-delay="250"
                        className="img-1"
                        src="/img/student-home/purple-img-4.png"
                        alt="event"
                      />
                      <img
                        data-aos="fade-left"
                        data-aos-delay="300"
                        className="img-4"
                        src="/img/student-home/purple-img-5.png"
                        alt="event"
                      />
                    </div>
                    <div className="left-img-frame aos-animate">
                      <p data-aos="fade-left" data-aos-delay="200">
                        Summary
                      </p>
                      <img
                        data-aos="fade-left"
                        data-aos-delay="250"
                        className="img-3"
                        src="/img/student-home/purple-img-3.png"
                        alt="summery"
                      />
                      <img
                        className="img-5"
                        data-aos="fade-left"
                        data-aos-delay="300"
                        src="/img/student-home/purple-img-6.png"
                        alt="summery"
                      />
                    </div>
                    <div className="left-img-frame aos-animate">
                      <p data-aos="fade-left" data-aos-delay="200">
                        Milestones
                      </p>
                      <img
                        className="img-1"
                        data-aos="fade-left"
                        data-aos-delay="250"
                        src="/img/student-home/purple-img-2.png"
                        alt="milestones"
                      />
                      <img
                        className="img-2"
                        data-aos="fade-left"
                        data-aos-delay="300"
                        src="/img/student-home/purple-img-1.png"
                        alt="milestones"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab-section */}

          <div className="tab-section">
            <div className="section pink-back mb-0 feature-wrapper build-section">
              <div id="reward" className="build-wrapper-box">
                <div className="floating-img">
                  <img
                    className="floating-animation reward-0"
                    src="/img/student-home/build-back-sm-0.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-1"
                    src="/img/student-home/build-back-sm-1.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-2"
                    src="/img/student-home/build-back-sm-2.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-3"
                    src="/img/student-home/build-back-sm-3.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-4"
                    src="/img/student-home/build-back-sm-4.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-5"
                    src="/img/student-home/build-back-sm-5.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-6"
                    src="/img/student-home/build-back-sm-4.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-7"
                    src="/img/student-home/build-back-sm-2.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-8"
                    src="/img/student-home/build-back-sm-1.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-9"
                    src="/img/student-home/build-back-sm-6.png"
                    alt="images"
                  />
                  <img
                    className="floating-animation reward-10"
                    src="/img/student-home/build-back-sm-7.png"
                    alt="images"
                  />
                </div>
                <div className="build-wrapper">
                  <div className="container">
                    <img
                      className="img-1"
                      data-aos="fade-up"
                      src="/img/student-home/build-mobile-3.webp"
                      alt="streaks"
                    />
                    <img
                      className="img-1 img-2"
                      data-aos="fade-up"
                      src="/img/student-home/build-mobile-2.webp"
                      alt="streaks"
                    />
                    <img
                      className="img-1 img-3"
                      data-aos="fade-up"
                      src="/img/student-home/build-mobile-1.webp"
                      alt="streaks"
                    />
                    <img
                      className="img-1 img-4"
                      data-aos="fade-up"
                      src="/img/student-home/build-mobile-4.webp"
                      alt="streaks"
                    />
                    <div className="data">
                      <h3>Key Features to Enhance Your Learning Journey</h3>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="shimmer-wrapper">
                            <img
                              src="/img/student-home/build-sm-3.png"
                              alt="Points"
                            />
                          </div>
                          <h4>Practice Points</h4>
                          <p>
                            Earn points by taking practice tests, climb the
                            leaderboard, and collect trophies for major
                            milestones.
                          </p>
                        </div>
                        <div className="col-md-3">
                          <div className="shimmer-wrapper">
                            <img
                              src="/img/student-home/build-sm-2.png"
                              alt="Consistency"
                            />
                          </div>
                          <h4>Coins of Consistency</h4>
                          <p>
                            Earn coins through learning activities and unlock
                            exclusive content and features.
                          </p>
                        </div>
                        <div className="col-md-3">
                          <div className="shimmer-wrapper">
                            <img
                              src="/img/student-home/build-sm-4.png"
                              alt="Trophies"
                            />
                          </div>
                          <h4>Earn Trophies</h4>
                          <p>
                            Scale the leaderboard with Practido Points, earn
                            trophies for achievements, and excel in your
                            learning journey.
                          </p>
                        </div>
                        <div className="col-md-3">
                          <div className="shimmer-wrapper">
                            <img
                              src="/img/student-home/build-sm-1.png"
                              alt="streaks"
                            />
                          </div>
                          <h4>Practice Streaks</h4>
                          <p>
                            Build consistency and boost success with our streak
                            feature, rewarding daily engagement.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mobile-build-box">
                  <div className="build-wrapper-mobile">
                    <div className="data">
                      <div className="row">
                        <Slider className="col-lg-12" {...buildSlider}>
                          <div className="slider-item">
                            <img
                              className="img-1"
                              src="/img/student-home/build-mobile-3.webp"
                              alt="Points"
                            />
                            <div className="slider-data">
                              <div className="mb-head">
                                Key Features to Enhance Your Learning Journey
                              </div>
                              <div className="shimmer-wrapper">
                                <img
                                  src="/img/student-home/build-sm-3.png"
                                  alt="Points"
                                />
                              </div>
                              <h4>Practice Points</h4>
                              <p>
                                Earn points by taking practice tests, climb the
                                leaderboard, and collect trophies for major
                                milestones.
                              </p>
                            </div>
                          </div>
                          <div className="slider-item item-1">
                            <img
                              className="img-1 img-2"
                              src="/img/student-home/build-mobile-2.webp"
                              alt="Consistency"
                            />
                            <div className="slider-data">
                              <div className="mb-head">
                                Key Features to Enhance Your Learning Journey
                              </div>
                              <div className="shimmer-wrapper">
                                <img
                                  src="/img/student-home/build-sm-2.png"
                                  alt="Consistency"
                                />
                              </div>
                              <h4>Coins of Consistency</h4>
                              <p>
                                Earn coins through learning activities and
                                unlock exclusive content and features.
                              </p>
                            </div>
                          </div>
                          <div className="slider-item item-2">
                            <img
                              className="img-1 img-4"
                              src="/img/student-home/build-mobile-4.webp"
                              alt="streaks"
                            />
                            <div className="slider-data">
                              <div className="mb-head">
                                Key Features to Enhance Your Learning Journey
                              </div>
                              <div className="shimmer-wrapper">
                                <img
                                  src="/img/student-home/build-sm-4.png"
                                  alt="Trophies"
                                />
                              </div>
                              <h4>Earn Trophies</h4>
                              <p>
                                Scale the leaderboard with Practido Points, earn
                                trophies for achievements, and excel in your
                                learning journey.
                              </p>
                            </div>
                          </div>
                          <div className="slider-item">
                            <img
                              className="img-1 img-3"
                              src="/img/student-home/build-mobile-1.webp"
                              alt="Trophies"
                            />
                            <div className="slider-data">
                              <div className="mb-head">
                                Key Features to Enhance Your Learning Journey
                              </div>
                              <div className="shimmer-wrapper">
                                <img
                                  src="/img/student-home/build-sm-1.png"
                                  alt="streaks"
                                />
                              </div>
                              <h4>Practice Streaks</h4>
                              <p>
                                Build consistency and boost success with our
                                streak feature, rewarding daily engagement.
                              </p>
                            </div>
                          </div>
                        </Slider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab-section */}

          <div className="tab-section">
            <div className="section orange-back mb-0 feature-wrapper mon-pd">
              <div className="container">
                <div id="monitor" className="feature-box row aos-animate">
                  <div
                    className="col-md-12 col-lg-5 para-box mon-para"
                    data-aos="fade-right"
                  >
                    <h3>Real-Time Monitoring</h3>
                    <p className="left">
                      Perfect for parents and teachers.<br/>
                      Track performance live, intervene early, and guide students smarter, not harder.
                    </p>
                    <h4>Free Learning Starts Now!</h4>
                    <AppStoreButtons variant="white" />
                  </div>
                  <div className="col-md-12 col-lg-7 center monitor-img-box">
                    <div className="monitor child-4" data-aos="fade-left">
                      <img src="/img/student-home/monitor-4.webp" alt="images" />
                    </div>
                    <div className="monitor" data-aos="fade-up">
                      <img src="/img/student-home/monitor-5.webp" alt="images" />
                    </div>
                    <div className="monitor child-5" data-aos="fade-up">
                      <img src="/img/student-home/monitor-6.webp" alt="images" />
                    </div>
                    <div className="monitor child-3" data-aos="fade-up">
                      <img src="/img/student-home/monitor-2.webp" alt="images" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab-section */}

          <div className="tab-section">
            <div className="section sky-blue-back mb-0 feature-wrapper parent-challenge">
              <div className="container">
                <div className="feature-box row aos-animate pd0 order-change">
                  <div
                    className="col-md-12 col-offset-1 col-lg-6  para-box"
                    data-aos="fade-right"
                  >
                    <h3>Leaderboard – Join the Top Performers</h3>
                    <p className="left">
                      Watch your rank rise as you practice more. Compete with peers, stay motivated, and celebrate your academic wins.
                    </p>
                    <h4>Free Learning Starts Now!</h4>
                    <AppStoreButtons variant="white" />
                  </div>
                  <div
                    className="col-md-12 col-lg-5 center"
                    data-aos="fade-left"
                  >
                    <img
                      className="feature-img"
                      src="/img/app-image/key-feature.webp"
                      alt="feature"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab-section */}
        </div>
        {/* feature-data */}
      </section>

      <SliderRating contactFormRef={contactFormRef} />
      <section className="faq-main-section faq-container">
        <div className="faq-data">
          <div className="container">
            <h3>Frequently Asked Questions</h3>       
            <div className="update-faq">
              <StudentAccordionPage></StudentAccordionPage>
            </div>
          </div>
        </div>
      </section>
      <ContactForm contactFormRef={setFormRef} />
    </>
  );
};

export default StudentLandingPage;
